import React, { useEffect, useRef, useState } from 'react';
import { SkeletonPlaceholder, Tab, Tabs } from 'carbon-components-react';
import { useHistory } from 'react-router-dom';
import ResponsePreviewCarousel from './ResponsePreviewCarousel';
import {
  ResponsePreviewNavigation,
  PreviousResponseButton,
  NextResponseButton
} from './ResponseNavigation';
import {
  ResponsePreview,
  ResponseBodyPreview,
  ResponseWebMapPreview,
  ResponsePdfPreview,
  ResponseNoPreview,
  ResponseLazyLoader,
  ResponseImagePreview
} from './ResponsePreviews';
import { getAttachmentData } from 'utilities/responseAttachment.util';
import { getAttachmentPreviewUrl } from 'services/enquiry.service';
import { isIOS } from 'react-device-detect';

const ReferralResponseList = ({
  referralIndex,
  responseGroups,
  initialSelectedResponse,
  selectedResponseGroup,
  isLoading,
  setShowResponseBodyModal,
  setShowResponseWebMapModal,
  setShowResponsePdfModal,
  setShowResponseImageModal,
  onClickResponsePreview,
  emailPreview,
  onResponseTabClick,
  onClickNextResponse,
  isNextResponseDisabled,
  onClickPreviousResponse,
  isPreviousResponseDisabled,
  showPreviousButton,
  showNextButton,
  enquiryId
}: {
  referralIndex: number;
  responseGroups: any[];
  // Response selection is done internally in this component. However, if the user clicks on a response from the
  // referral overview page, the selected response (which is the latest response for a referral) is passed in as a prop.
  initialSelectedResponse: number;
  selectedResponseGroup: any[];
  isLoading: boolean;
  setShowResponseBodyModal: (show: boolean) => void;
  setShowResponseWebMapModal: (show: boolean) => void;
  setShowResponsePdfModal: (show: boolean) => void;
  setShowResponseImageModal: (show: boolean) => void;
  onClickResponsePreview: Function;
  enquiryId?: number;
  emailPreview?: any;
  onResponseTabClick: Function;
  onClickNextResponse?: () => void;
  isNextResponseDisabled?: boolean;
  onClickPreviousResponse?: () => void;
  isPreviousResponseDisabled?: boolean;
  showPreviousButton?: boolean;
  showNextButton?: boolean;
}) => {
  const history = useHistory();
  const queryParams = new URLSearchParams(history?.location.search);
  const shareId = queryParams.get('shareId') || '';

  const isMultiResponse = responseGroups.length > 1;
  const carouselRefs = useRef<{
    [key: number]: React.RefObject<HTMLDivElement>;
  }>({});

  const [responseIndex, setResponseIndex] = useState(
    initialSelectedResponse >= 0 ? initialSelectedResponse : 0
  );
  const [isChangingResponseTab, setIsChangingResponseTab] = useState(false);

  // State for tracking PDF loading for iOS
  const [pdfLoadingStates, setPdfLoadingStates] = useState<{
    [key: string]: boolean;
  }>({});

  // Initialize refs for each response
  useEffect(() => {
    responseGroups.forEach((_, index) => {
      if (!carouselRefs.current[index]) {
        carouselRefs.current[index] = React.createRef<HTMLDivElement>();
      }
    });
  }, [responseGroups]);

  useEffect(() => {
    if (!isLoading) {
      setIsChangingResponseTab(false);
    }
  }, [isLoading]);

  const handlePdfPreviewClick = async (
    fileId: number,
    responseValue: string,
    attachmentIndex: number
  ) => {
    // SENT-2395
    // For iOS, we want to open the PDF in a new tab using the browser viewer and not in a modal
    // This is an attempt to reduce the crashing issue observed in iOS-based devices.
    // The problem is, the PDF URL needs to be generated server-side, and iOS Safari will block the
    // operation if the click > url generation > window open is not synchronous.
    // As a workaround, we did this:
    // 1. Generate presigned URLs on-demand (on click)
    // 2. Open blank window synchronously with click event (bypasses iOS popup blocker)
    // 3. Fetch presigned URL async after window opens
    // 4. Use location.replace() to navigate (prevents rare 3-tab bug)
    // 5. Show "Loading PDF..." message while fetching to improve user experience

    if (isIOS && enquiryId && fileId) {
      const key = `pdf-${fileId}-${attachmentIndex}`;

      // Prevent multiple simultaneous clicks
      if (pdfLoadingStates[key]) return;

      let newWindow: Window | null = null;

      try {
        setPdfLoadingStates((prev) => ({ ...prev, [key]: true }));

        // CRITICAL: Open the window SYNCHRONOUSLY with the user gesture
        // This prevents iOS Safari from blocking the popup
        newWindow = window.open('about:blank', '_blank');

        // If popup was blocked (shouldn't happen since it's synchronous)
        if (!newWindow) {
          onClickResponsePreview(responseValue);
          setShowResponsePdfModal(true);
          return;
        }

        // Show a loading message in the blank window while fetching
        newWindow.document.write(
          '<html><body style="display: flex; align-items: center; justify-content: center; height: 100vh; font-family: sans-serif; color: #666;">Loading PDF...</body></html>'
        );

        // Fetch the presigned URL asynchronously
        const response = await getAttachmentPreviewUrl(
          fileId,
          shareId,
          enquiryId.toString()
        );

        if (response?.url) {
          // Navigate to the PDF using replace() for more reliable behavior on iOS Safari
          newWindow.location.replace(response.url);
        } else {
          // If URL generation failed, close the window and fall back to modal
          try {
            newWindow.close();
          } catch (closeError) {
            console.error('Failed to close window:', closeError);
          }
          onClickResponsePreview(responseValue);
          setShowResponsePdfModal(true);
        }
      } catch (error) {
        console.error('Failed to fetch PDF URL:', error);
        // Try to close the blank window if it was opened
        if (newWindow) {
          try {
            newWindow.close();
          } catch (closeError) {
            console.error('Failed to close window:', closeError);
          }
        }
        // Fallback to modal on error
        onClickResponsePreview(responseValue);
        setShowResponsePdfModal(true);
      } finally {
        setPdfLoadingStates((prev) => ({ ...prev, [key]: false }));
      }
    } else {
      // For non-iOS: open modal (original behavior)
      onClickResponsePreview(responseValue);
      setShowResponsePdfModal(true);
    }
  };

  const scrollLeft = () => {
    const currentCarouselRef = carouselRefs.current[responseIndex];
    if (currentCarouselRef?.current) {
      const element = currentCarouselRef.current;
      const { scrollLeft } = element;

      // Check if we're at the start of the scroll
      const atStart = scrollLeft <= 1;

      if (atStart && showPreviousButton) {
        // Trigger navigation to previous response/referral group
        handleScrollPastStart();
      } else {
        // Normal scroll behavior
        const scrollAmount = 200; // Adjust scroll distance as needed
        element.scrollBy({
          left: -scrollAmount,
          behavior: 'smooth'
        });
      }
    }
  };

  const scrollRight = () => {
    const currentCarouselRef = carouselRefs.current[responseIndex];
    if (currentCarouselRef?.current) {
      const element = currentCarouselRef.current;
      const { scrollLeft, scrollWidth, clientWidth } = element;

      // Check if we're at the end of the scroll
      const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;

      if (atEnd && showNextButton) {
        // Trigger navigation to next response/referral group
        handleScrollPastEnd();
      } else {
        // Normal scroll behavior
        const scrollAmount = 200; // Adjust scroll distance as needed
        element.scrollBy({
          left: scrollAmount,
          behavior: 'smooth'
        });
      }
    }
  };

  const handleResponseTabChange = (index: number) => {
    setIsChangingResponseTab(true);
    setResponseIndex(index);
    onResponseTabClick(referralIndex, index);
  };

  const handleScrollPastEnd = () => {
    if (isMultiResponse && responseIndex < responseGroups.length - 1) {
      handleResponseTabChange(responseIndex + 1);
    } else {
      if (onClickNextResponse && !isNextResponseDisabled) {
        onClickNextResponse();
      }
    }
  };

  const handleScrollPastStart = () => {
    if (isMultiResponse && responseIndex > 0) {
      handleResponseTabChange(responseIndex - 1);
    } else {
      if (onClickPreviousResponse && !isPreviousResponseDisabled) {
        onClickPreviousResponse();
      }
    }
  };

  return (
    <div className="mobile-view-referral-response-list">
      <div className="mobile-view-content-header">
        <div className="mobile-view-content-title">Content</div>
        <div className="mobile-view-content-description">
          Click on a thumbnail below to view the full content.
        </div>
      </div>
      {isLoading && !isChangingResponseTab ? (
        <SkeletonPlaceholder className="mobile-view-response-list-skeleton" />
      ) : (
        <div className="mobile-view-response-list-container">
          <Tabs
            className={`mobile-view-response-list-tabs ${
              isMultiResponse ? '' : 'mobile-view-response-list-tabs--hidden'
            }`}
            onSelectionChange={(index) => handleResponseTabChange(index)}
            selected={responseIndex}
          >
            {responseGroups
              .sort((a: any, b: any) => a.responseId - b.responseId)
              .map((responseGroup: any, index: number) => {
                return (
                  <Tab
                    id={`response-${index}`}
                    label={`Response ${index + 1}`}
                    key={`response-${index}`}
                  >
                    {isChangingResponseTab ? (
                      <div className="mobile-view-response-list-tab-content">
                        <ResponsePreviewCarousel>
                          {showPreviousButton && onClickPreviousResponse && (
                            <div className="mobile-view-response-nav-item">
                              <PreviousResponseButton
                                onClick={() => {
                                  if (isMultiResponse && index > 0) {
                                    handleResponseTabChange(index - 1);
                                  } else {
                                    onClickPreviousResponse();
                                  }
                                }}
                                disabled={isPreviousResponseDisabled || false}
                              />
                            </div>
                          )}
                          <ResponsePreview
                            key={'loading-1'}
                            label={'Loading...'}
                            isLoading={true}
                            content={<></>}
                          />
                          <ResponsePreview
                            key={'loading-2'}
                            label={'Loading...'}
                            isLoading={true}
                            content={<></>}
                          />
                          {showNextButton && onClickNextResponse && (
                            <div className="mobile-view-response-nav-item">
                              <NextResponseButton
                                onClick={() => {
                                  if (
                                    isMultiResponse &&
                                    index < responseGroups.length - 1
                                  ) {
                                    handleResponseTabChange(index + 1);
                                  } else {
                                    onClickNextResponse();
                                  }
                                }}
                                disabled={isNextResponseDisabled || false}
                              />
                            </div>
                          )}
                        </ResponsePreviewCarousel>
                        {/* Dummy div to make the loading container match the 
                        height of the non-loading container */}
                        <div style={{ height: '40px' }}></div>
                      </div>
                    ) : (
                      responseIndex === index && (
                        <div className="mobile-view-response-list-tab-content">
                          <ResponsePreviewCarousel
                            scrollRef={carouselRefs.current[index]}
                            onScrollPastEnd={
                              showNextButton ? handleScrollPastEnd : undefined
                            }
                            onScrollPastStart={
                              showPreviousButton
                                ? handleScrollPastStart
                                : undefined
                            }
                            showPreviousOverscroll={showPreviousButton}
                            showNextOverscroll={showNextButton}
                          >
                            {showPreviousButton && onClickPreviousResponse && (
                              <div className="mobile-view-response-nav-item">
                                <PreviousResponseButton
                                  onClick={() => {
                                    if (isMultiResponse && index > 0) {
                                      handleResponseTabChange(index - 1);
                                    } else {
                                      onClickPreviousResponse();
                                    }
                                  }}
                                  disabled={isPreviousResponseDisabled || false}
                                />
                              </div>
                            )}
                            {selectedResponseGroup.map(
                              (response: any, index: number) => {
                                const valueSplit = response.value.split('|');
                                const responseType = valueSplit[0];

                                // Only for attachment type
                                const attachmentIndex = Number(valueSplit[1]);
                                const attachmentData =
                                  responseType === 'attachment'
                                    ? getAttachmentData(
                                        attachmentIndex,
                                        emailPreview
                                      )
                                    : {
                                        data: {
                                          id: undefined,
                                          body: undefined,
                                          type: undefined,
                                          name: undefined
                                        },
                                        isFileSupported: false
                                      };
                                // Check if the image is a TIFF file
                                // TIFF is not supported by the ImagePreview component
                                const isTiffImage =
                                  attachmentData.data.type === 'image' &&
                                  attachmentData.data.name
                                    .toLowerCase()
                                    .endsWith('.tiff');

                                return (
                                  <ResponseLazyLoader
                                    key={`${response.label}-${index}`}
                                    rootMargin="100px"
                                  >
                                    <ResponsePreview
                                      label={response.label}
                                      isLoading={isLoading}
                                      content={
                                        <>
                                          {responseType === 'webMap' && (
                                            <ResponseWebMapPreview
                                              onClick={() => {
                                                onClickResponsePreview(
                                                  response.value
                                                );
                                                setShowResponseWebMapModal(
                                                  true
                                                );
                                              }}
                                            />
                                          )}
                                          {responseType === 'responseBody' && (
                                            <ResponseBodyPreview
                                              onClick={() => {
                                                onClickResponsePreview(
                                                  response.value
                                                );
                                                setShowResponseBodyModal(true);
                                              }}
                                              responseId={
                                                responseGroup.responseId
                                              }
                                              enquiryId={enquiryId}
                                            />
                                          )}
                                          {responseType === 'attachment' &&
                                            (attachmentData.data.type ===
                                            'pdf' ? (
                                              <ResponsePdfPreview
                                                onClick={(e) => {
                                                  e?.preventDefault?.();
                                                  e?.stopPropagation?.();
                                                  if (attachmentData.data.id) {
                                                    handlePdfPreviewClick(
                                                      attachmentData.data.id,
                                                      response.value,
                                                      attachmentIndex
                                                    );
                                                  }
                                                }}
                                                fileId={attachmentData.data.id}
                                                responseId={
                                                  responseGroup.responseId
                                                }
                                                enquiryId={enquiryId}
                                                isLoadingForIOS={
                                                  attachmentData.data.id
                                                    ? pdfLoadingStates[
                                                        `pdf-${attachmentData.data.id}-${attachmentIndex}`
                                                      ] || false
                                                    : false
                                                }
                                              />
                                            ) : attachmentData.data.type ===
                                                'image' && !isTiffImage ? (
                                              <ResponseImagePreview
                                                onClick={() => {
                                                  onClickResponsePreview(
                                                    response.value
                                                  );
                                                  setShowResponseImageModal(
                                                    true
                                                  );
                                                }}
                                                fileId={attachmentData.data.id}
                                                responseId={
                                                  responseGroup.responseId
                                                }
                                                enquiryId={enquiryId}
                                              />
                                            ) : isTiffImage ? (
                                              <ResponseNoPreview
                                                url={
                                                  attachmentData.data.body ||
                                                  '#'
                                                }
                                              />
                                            ) : (
                                              <ResponseNoPreview
                                                url={
                                                  attachmentData.data.body ||
                                                  '#'
                                                }
                                              />
                                            ))}
                                          {responseType === 'link' && (
                                            <ResponseNoPreview
                                              url={valueSplit[1]}
                                            />
                                          )}
                                        </>
                                      }
                                    />
                                  </ResponseLazyLoader>
                                );
                              }
                            )}
                            {showNextButton && onClickNextResponse && (
                              <div className="mobile-view-response-nav-item">
                                <NextResponseButton
                                  onClick={() => {
                                    if (
                                      isMultiResponse &&
                                      index < responseGroups.length - 1
                                    ) {
                                      handleResponseTabChange(index + 1);
                                    } else {
                                      onClickNextResponse();
                                    }
                                  }}
                                  disabled={isNextResponseDisabled || false}
                                />
                              </div>
                            )}
                          </ResponsePreviewCarousel>
                          <ResponsePreviewNavigation
                            onScrollLeft={scrollLeft}
                            onScrollRight={scrollRight}
                          />
                        </div>
                      )
                    )}
                  </Tab>
                );
              })}
          </Tabs>
        </div>
      )}
    </div>
  );
};

export default ReferralResponseList;
